import _ from 'lodash';
import React, {FC} from 'react';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {useInitialEffect} from '../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {useUnmountCancelToken} from '../../../srcCommon/helpers/hookHelpers';
import {InboxesCell} from '../../components/common/table/cells/inboxesCell';
import {TableCellProps} from '../../components/common/table/cells/tableCell';
import {isTeammateNamespace} from '../../helpers/models/namespaceHelpers';
import {ResolveDispatchProps} from '../../helpers/reduxHelpers';
import {makeMapDispatchToProps, makeMapStateToProps, ResolveStateProps} from '../../helpers/reselectHelpers';
import {TagModel} from '../../models/api/tagModel';
import {AppState} from '../../store/appState';
import {getCompanyUrl} from '../../store/data/company/companySelectors';
import {fetchTagInboxesAsyncAction} from '../../store/data/inboxes/inboxesActions';
import {getInboxes} from '../../store/data/inboxes/inboxesSelectors';

/*
 * Props.
 */

interface SettingsTagInboxesCellContainerProps extends TableCellProps<TagModel> {}

interface SettingsTagInboxesCellProps
  extends SettingsTagInboxesCellContainerProps,
    ResolveStateProps<typeof mapStateToProps>,
    ResolveDispatchProps<typeof mapDispatchToProps> {}

/*
 * Selectors.
 */

function getTag(_state: AppState, props: SettingsTagInboxesCellContainerProps) {
  return props.value;
}

function makeGetTagInboxes() {
  return createSelector([getTag, getInboxes], (tag, inboxes) => {
    if (!tag || tag.isVisibleInAllInboxes) {
      return undefined;
    }

    return _(tag.inboxIds)
      .map((id) => inboxes[id])
      .compact()
      .sortBy((i) => i.name.toLowerCase())
      .value();
  });
}

/*
 * Component.
 */

const SettingsTagInboxesCell: FC<SettingsTagInboxesCellProps> = (props) => {
  const {
    isSelected,
    isHovering,
    isLoading,
    size,
    settings,
    index,
    shouldShowStickyGradient,
    shouldRemoveTopBorderRadius,
    shouldRemoveBottomBorderRadius,
    tagInboxes,
    fetchTagInboxesAsync,
  } = props;
  const tag = props.value;
  const cancelToken = useUnmountCancelToken();

  useInitialEffect(() => {
    // If we're in a team namespace and we're missing inboxes, fetch and update inboxes.
    const isTeamNamespace = tag && !isTeammateNamespace(tag.namespace);
    const didFindAllInboxes = tag && tagInboxes && tag.inboxIds.length === tagInboxes.length;
    if (isTeamNamespace && !didFindAllInboxes && !tag.isVisibleInAllInboxes) {
      fetchTagInboxesAsync(tag, cancelToken);
    }
  });

  return (
    <InboxesCell
      isSelected={isSelected}
      isHovering={isHovering}
      isLoading={isLoading}
      size={size}
      settings={settings}
      index={index}
      inboxes={tagInboxes}
      value={tag}
      shouldShowStickyGradient={shouldShowStickyGradient}
      shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
      shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<SettingsTagInboxesCellContainerProps>()(() => ({
  tagInboxes: makeGetTagInboxes(),
  companyUrl: getCompanyUrl,
}));

const mapDispatchToProps = makeMapDispatchToProps<SettingsTagInboxesCellContainerProps>()(() => ({
  fetchTagInboxesAsync: fetchTagInboxesAsyncAction,
}));

export const SettingsTagInboxesCellContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(SettingsTagInboxesCell);
